import type { ReactNode } from "react";
import { ChevronDown, ChevronUp, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useCollapsedFilters } from "@/hooks/useCollapsedFilters";
import { cn } from "@/lib/utils";

export interface CollapsibleFilterBarProps {
  /** Unique key for localStorage persistence */
  storageKey: string;
  /** Number of active filters to display in badge */
  activeFilterCount: number;
  /** Search section content (always visible) */
  searchSection: ReactNode;
  /** Filter content to be collapsed */
  children: ReactNode;
  /** Initial collapsed state if no stored value (default: false) */
  defaultCollapsed?: boolean;
  /** Callback when collapsed state changes */
  onCollapsedChange?: (collapsed: boolean) => void;
  /** Additional CSS classes for the Card container */
  className?: string;
}

/**
 * A collapsible filter bar component that shows a search section always visible
 * and allows expanding/collapsing additional filters with smooth animation.
 * The collapsed state is persisted to localStorage.
 */
export function CollapsibleFilterBar({
  storageKey,
  activeFilterCount,
  searchSection,
  children,
  defaultCollapsed = false,
  onCollapsedChange,
  className,
}: CollapsibleFilterBarProps) {
  const { isCollapsed, setIsCollapsed, isInitialized } = useCollapsedFilters({
    storageKey,
    defaultCollapsed,
  });

  const handleCollapsedChange = (open: boolean) => {
    const collapsed = !open;
    setIsCollapsed(collapsed);
    onCollapsedChange?.(collapsed);
  };

  // Don't render until initialized to prevent flash of wrong state
  if (!isInitialized) {
    return (
      <Card className={cn("p-3 sm:p-4 mb-4 sm:mb-6", className)}>
        <div className="space-y-3 sm:space-y-4">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <div className="w-full sm:max-w-sm lg:max-w-md">{searchSection}</div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className={cn("p-3 sm:p-4 mb-4 sm:mb-6", className)}>
      <Collapsible open={!isCollapsed} onOpenChange={handleCollapsedChange}>
        <div className="space-y-3 sm:space-y-4">
          {/* Always visible: Search section + expand/collapse button */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <div className="w-full sm:max-w-sm lg:max-w-md">{searchSection}</div>
            <div className="flex items-end">
              <CollapsibleTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full sm:w-auto gap-2"
                  aria-expanded={!isCollapsed}
                >
                  <Filter className="h-4 w-4" />
                  <span>Filters</span>
                  {activeFilterCount > 0 && (
                    <Badge
                      variant="secondary"
                      className="ml-1 h-5 min-w-5 px-1.5 text-xs font-medium"
                    >
                      {activeFilterCount}
                    </Badge>
                  )}
                  {isCollapsed ? (
                    <ChevronDown className="h-4 w-4 ml-1" />
                  ) : (
                    <ChevronUp className="h-4 w-4 ml-1" />
                  )}
                </Button>
              </CollapsibleTrigger>
            </div>
          </div>

          {/* Collapsible filter content */}
          <CollapsibleContent className="CollapsibleContent">
            <div className="pt-1">{children}</div>
          </CollapsibleContent>
        </div>
      </Collapsible>
    </Card>
  );
}
